/**
 * ImplementationProgress Component
 * 
 * Displays strategy implementation progress with visual tracking.
 * Shows overall progress bar and individual strategy status.
 * 
 * Features:
 * - Overall progress percentage with bar
 * - Strategy list with status indicators
 * - Completed (green check), In Progress (blue clock), Pending (gray)
 * - Savings amount for each strategy
 * - "View All Strategies" button
 * - Responsive design
 * 
 * Created: 2025-01-16 (Dashboard Redesign Phase 2)
 * Phase: Dashboard Redesign - Phase 2: Content Cards
 */

import React from 'react';

const ImplementationProgress = ({ overallProgress, strategies }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return (
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'in_progress':
        return (
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'pending':
        return (
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const getStatusText = (status, progress) => {
    switch (status) {
      case 'completed':
        return <span className="text-xs text-green-600 font-medium">Completed</span>;
      case 'in_progress':
        return <span className="text-xs text-blue-600 font-medium">In Progress ({progress}%)</span>;
      case 'pending':
        return <span className="text-xs text-gray-500">Not Started</span>;
      default:
        return null;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Implementation Progress</h3>
          <p className="text-sm text-gray-600">Track your tax strategy rollout</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-blue-600">{overallProgress}%</div>
          <p className="text-xs text-gray-500">Overall</p>
        </div>
      </div>

      {/* Overall Progress Bar */}
      <div className="mb-6">
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-blue-500 to-teal-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
      </div>

      {/* Strategy List */}
      <div className="space-y-4">
        {(strategies || []).map((strategy, index) => (
          <div 
            key={strategy.id || index}
            className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {/* Status Icon */}
            {getStatusIcon(strategy.status)}

            {/* Strategy Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">
                    {strategy.name}
                  </h4>
                  {getStatusText(strategy.status, strategy.progress)}
                </div>
                <div className="text-right ml-4">
                  <div className="text-sm font-bold text-gray-900">
                    {formatCurrency(strategy.savings)}
                  </div>
                  <p className="text-xs text-gray-500">
                    {strategy.status === 'completed' ? 'Saved' : 'Potential'}
                  </p>
                </div>
              </div>
              
              {/* Progress bar for in_progress items */}
              {strategy.status === 'in_progress' && strategy.progress && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${strategy.progress}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* View All Button */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <button className="w-full py-2 px-4 bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium rounded-lg transition-colors text-sm">
          View All Strategies →
        </button>
      </div>
    </div>
  );
};

// Default props
ImplementationProgress.defaultProps = {
  overallProgress: 38,
  strategies: [
    {
      id: 1,
      name: 'S-Corporation Election',
      status: 'completed',
      savings: 8500,
    },
    {
      id: 2,
      name: 'Home Office Deduction',
      status: 'completed',
      savings: 3200,
    },
    {
      id: 3,
      name: 'Business Equipment Purchase',
      status: 'in_progress',
      progress: 75,
      savings: 12000,
    },
  ],
};

export default ImplementationProgress;
